/**
 * Centralized logging abstraction.
 *
 * Replaces scattered `console.*` calls so log verbosity lives in one place
 * instead of shipping ~200 unconditional `console.log`s to production. Rules:
 *
 * - `debug` / `log` / `info` — informational; emitted only in development, or
 *   in any environment when the field-debug flag `window.__CSM_VERBOSE__` is
 *   set to `true`. Suppressed in production and in the test runner by default.
 * - `warn` / `error` — always emitted (real problems must always surface in
 *   the browser console / error reporting), in every environment.
 *
 * Usage: `import { logger } from "utility/logger";` then `logger.error(...)`.
 * The signature is a drop-in for `console.*` (variadic, any args).
 */

type LogFn = (...args: unknown[]) => void;

const isDevelopment = process.env.NODE_ENV === "development";

/**
 * Opt-in verbose logging for production field debugging: set
 * `window.__CSM_VERBOSE__ = true` in DevTools to reveal informational logs.
 */
function verboseEnabled(): boolean {
  try {
    return typeof window !== "undefined" &&
      (window as unknown as { __CSM_VERBOSE__?: boolean }).__CSM_VERBOSE__ === true;
  } catch {
    return false;
  }
}

function infoLevel(method: "log" | "info" | "debug"): LogFn {
  return (...args: unknown[]) => {
    if (isDevelopment || verboseEnabled()) {
      // eslint-disable-next-line no-console
      console[method](...args);
    }
  };
}

export const logger = {
  debug: infoLevel("debug"),
  log: infoLevel("log"),
  info: infoLevel("info"),
  warn: ((...args: unknown[]) => {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }) as LogFn,
  error: ((...args: unknown[]) => {
    // eslint-disable-next-line no-console
    console.error(...args);
  }) as LogFn,
};

export default logger;
